import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  useTheme,
  TableBody,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  TableSortLabel,
  TablePagination,
  useMediaQuery,
  IconButton,
  Collapse,
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DescriptionIcon from "@mui/icons-material/Description";
import TableViewIcon from "@mui/icons-material/TableView";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "../../utils/axiosInstance";
import { toast } from "react-toastify";

const MonthlyReports = ({ units, loadingUnitSearchData }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const getLastSixMonths = () => {
    const allMonths = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const now = new Date();
    const currentMonth = now.getMonth();
    const result = [];

    for (let i = 1; i <= 6; i++) {
      const d = new Date(now.getFullYear(), currentMonth - i, 1);
      result.push({
        label: `${allMonths[d.getMonth()]}`,
        value: d.getMonth() + 1,
        year: d.getFullYear(),
      });
    }

    return result.reverse();
  };

  const lastSixMonths = getLastSixMonths();
  const [selectedMonth, setSelectedMonth] = useState(lastSixMonths[5].value);
  const [selectedYear, setSelectedYear] = useState(lastSixMonths[5].year);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportData, setReportData] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("Unit Name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const unitMap =
    units?.reduce((acc, u) => {
      acc[u.id] = u.name;
      return acc;
    }, {}) || {};

  const filteredReportData = reportData
    ? Object.entries(reportData).filter(([unitId]) =>
        (unitMap[unitId] || unitId)
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      )
    : [];

  const fetchMonthlyReport = async (month, year) => {
    if (!units || units.length === 0) return;
    try {
      setLoadingReport(true);
      setError(null);

      const payload = {
        year,
        month,
        units: units.map((u) => u.id),
      };
      const response = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/reports/precision-report-month`,
        payload,
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        }
      );

      setReportData(response.data.data.mydict);
    } catch (err) {
      console.error("Failed to fetch report data:", err);
      setError("Error fetching report data");
    } finally {
      setLoadingReport(false);
    }
  };

  useEffect(() => {
    if (units && units.length > 0) {
      fetchMonthlyReport(selectedMonth, selectedYear);
    }
  }, [selectedMonth, units]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const copyToClipboard = () => {
    if (!sortedData || sortedData.length === 0) {
      console.warn("No report data to copy");
      return;
    }
    const textContent = sortedData
      .map(([unitId, report]) => {
        return `${unitMap[unitId] || unitId}\t${report.year}\t${
          report.month
        }\t${report.down}\t${report.up}\t${report.customer}\t${
          report.precision
        }\t${report.shutDowns}`;
      })
      .join("\n");

    const headers =
      "Unit Name\tYear\tMonth\tStop(Hrs)\tRun(Hrs)\tCustomer(Hrs)\tPRC(Hrs)\tShutdowns";

    navigator.clipboard
      .writeText(`${headers}\n${textContent}`)
      .then(() => toast.success(`Monthly report copied to clipboard`))
      .catch((err) => console.error("Failed to copy:", err));

    handleExportMenuClose();
  };

  const exportToCSV = () => {
    if (!sortedData || sortedData.length === 0) {
      console.warn("No report data to export");
      return;
    }
    const csvContent = [
      [
        "Unit Name",
        "Year",
        "Month",
        "Stop (Hrs)",
        "Run (Hrs)",
        "Customer (Hrs)",
        "PRC (Hrs)",
        "Shutdowns",
      ],
      ...sortedData.map(([unitId, report]) => [
        unitMap[unitId] || unitId,
        report.year,
        report.month,
        report.down,
        report.up,
        report.customer,
        report.precision,
        report.shutDowns,
      ]),
    ];

    const csvString = csvContent
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Monthly_Report.csv`;
    link.click();

    handleExportMenuClose();
    toast.success("Exported Monthly Report to CSV");
  };

  const exportToExcel = () => {
    if (!sortedData || sortedData.length === 0) {
      console.warn("No report data to export");
      return;
    }

    const headers = [
      "Unit Name",
      "Year",
      "Month",
      "Stop (Hrs)",
      "Run (Hrs)",
      "Customer (Hrs)",
      "PRC (Hrs)",
      "Shutdowns",
    ];
    const rows = sortedData.map(([unitId, report]) => [
      unitMap[unitId] || unitId,
      report.year,
      report.month,
      report.down,
      report.up,
      report.customer,
      report.precision,
      report.shutDowns,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Monthly Report");
    XLSX.writeFile(workbook, `Monthly_Report.xlsx`);

    handleExportMenuClose();
    toast.success("Exported Monthly Report to Excel");
  };

  const exportToPDF = () => {
    if (!sortedData || sortedData.length === 0) {
      console.warn("No report data to export");
      return;
    }

    const doc = new jsPDF("l", "pt", "a4");
    doc.setFontSize(12);
    doc.text("Monthly Units Report", 40, 30);

    const headers = [
      [
        "Unit Name",
        "Year",
        "Month",
        "Stop (Hrs)",
        "Run (Hrs)",
        "Customer (Hrs)",
        "PRC (Hrs)",
        "Shutdowns",
      ],
    ];

    const rows = sortedData.map(([unitId, report]) => [
      unitMap[unitId] || unitId,
      report.year,
      new Date(report.year, report.month - 1).toLocaleString("default", {
        month: "long",
      }),
      report.down,
      report.up,
      report.customer,
      report.precision,
      report.shutDowns,
    ]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 50,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: "linebreak",
      },
      headStyles: { fillColor: [41, 128, 185] },
      tableWidth: "auto",
      columnStyles: {
        0: { cellWidth: 90 },
        1: { cellWidth: 50 },
        2: { cellWidth: 60 },
        3: { cellWidth: 70 },
        4: { cellWidth: 70 },
        5: { cellWidth: 90 },
        6: { cellWidth: 70 },
        7: { cellWidth: 70 },
      },
    });

    doc.save(`Monthly_Report.pdf`);
    handleExportMenuClose();
    toast.success(`Exported Monthly Report to PDF`);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedData = React.useMemo(() => {
    if (!filteredReportData) return [];

    return [...filteredReportData].sort(([aId, aReport], [bId, bReport]) => {
      let aValue, bValue;

      switch (orderBy) {
        case "Unit Name":
          aValue = unitMap[aId] || aId;
          bValue = unitMap[bId] || bId;
          break;
        case "Year":
          aValue = aReport.year;
          bValue = bReport.year;
          break;
        case "Month":
          aValue = aReport.month;
          bValue = bReport.month;
          break;
        case "Stop(Hrs)":
          aValue = aReport.down;
          bValue = bReport.down;
          break;
        case "Run(Hrs)":
          aValue = aReport.up;
          bValue = bReport.up;
          break;
        case "Customer(Hrs)":
          aValue = aReport.customer;
          bValue = bReport.customer;
          break;
        case "PRC(Hrs)":
          aValue = aReport.precision;
          bValue = bReport.precision;
          break;
        case "Shutdowns":
          aValue = aReport.shutDowns;
          bValue = bReport.shutDowns;
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredReportData, order, orderBy]);

  const paginatedUnits = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const renderTable = () => (
    <TableContainer
      sx={{
        pl: 2,
        flexGrow: 1,
        height: { xs: "50vh", md: "63vh" },
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
        overscrollBehaviorY: "none",
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
            {/* Mobile expand button column */}
            <TableCell
              sx={{
                display: { xs: "table-cell", md: "none" },
                fontWeight: "bold",
                textAlign: "center",
                fontSize: "12px",
                padding: "12px 8px",
                width: "50px",
              }}
            >
              Details
            </TableCell>

            <TableCell
              sx={{
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                padding: "8px",
              }}
            >
              <TableSortLabel
                active={orderBy === "Unit Name"}
                direction={orderBy === "Unit Name" ? order : "asc"}
                onClick={() => handleRequestSort("Unit Name")}
              >
                Unit Name
              </TableSortLabel>
            </TableCell>

            <TableCell
              sx={{
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                padding: "8px",
              }}
            >
              <TableSortLabel
                active={orderBy === "Year"}
                direction={orderBy === "Year" ? order : "asc"}
                onClick={() => handleRequestSort("Year")}
              >
                Year
              </TableSortLabel>
            </TableCell>

            <TableCell
              sx={{
                display: { xs: "none", md: "table-cell" },
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                padding: "8px",
              }}
            >
              <TableSortLabel
                active={orderBy === "Month"}
                direction={orderBy === "Month" ? order : "asc"}
                onClick={() => handleRequestSort("Month")}
              >
                Month
              </TableSortLabel>
            </TableCell>

            <TableCell
              sx={{
                display: { xs: "none", md: "table-cell" },
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                padding: "8px",
              }}
            >
              <TableSortLabel
                active={orderBy === "Stop(Hrs)"}
                direction={orderBy === "Stop(Hrs)" ? order : "asc"}
                onClick={() => handleRequestSort("Stop(Hrs)")}
              >
                Stop(Hrs)
              </TableSortLabel>
            </TableCell>

            <TableCell
              sx={{
                display: { xs: "none", md: "table-cell" },
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                padding: "8px",
              }}
            >
              <TableSortLabel
                active={orderBy === "Run(Hrs)"}
                direction={orderBy === "Run(Hrs)" ? order : "asc"}
                onClick={() => handleRequestSort("Run(Hrs)")}
              >
                Run(Hrs)
              </TableSortLabel>
            </TableCell>

            <TableCell
              sx={{
                display: { xs: "none", md: "table-cell" },
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                padding: "8px",
              }}
            >
              <TableSortLabel
                active={orderBy === "Customer(Hrs)"}
                direction={orderBy === "Customer(Hrs)" ? order : "asc"}
                onClick={() => handleRequestSort("Customer(Hrs)")}
              >
                Customer(Hrs)
              </TableSortLabel>
            </TableCell>

            <TableCell
              sx={{
                display: { xs: "none", md: "table-cell" },
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                padding: "8px",
              }}
            >
              <TableSortLabel
                active={orderBy === "PRC(Hrs)"}
                direction={orderBy === "PRC(Hrs)" ? order : "asc"}
                onClick={() => handleRequestSort("PRC(Hrs)")}
              >
                PRC(Hrs)
              </TableSortLabel>
            </TableCell>

            <TableCell
              sx={{
                display: { xs: "none", md: "table-cell" },
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                padding: "8px",
              }}
            >
              <TableSortLabel
                active={orderBy === "Shutdowns"}
                direction={orderBy === "Shutdowns" ? order : "asc"}
                onClick={() => handleRequestSort("Shutdowns")}
              >
                Shutdowns
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedUnits.length > 0 ? (
            paginatedUnits.map(([unitId, report], index) => (
              <React.Fragment key={unitId}>
                <TableRow
                  hover
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                    "&:hover": {
                      backgroundColor: "#4a4d54",
                    },
                  }}
                >
                  {/* Expand Button - Mobile Only */}
                  <TableCell
                    sx={{
                      display: { xs: "table-cell", md: "none" },
                      textAlign: "center",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => toggleRowExpansion(unitId)}
                      sx={{ color: "white" }}
                    >
                      {expandedRows[unitId] ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </TableCell>

                  <TableCell
                    sx={{
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    {unitMap[unitId] || unitId}
                  </TableCell>

                  <TableCell
                    sx={{
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    {report.year}
                  </TableCell>

                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    {new Date(report.year, report.month - 1).toLocaleString(
                      "default",
                      {
                        month: "long",
                      }
                    )}
                  </TableCell>

                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    {report.down}
                  </TableCell>

                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    {report.up}
                  </TableCell>

                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    {report.customer}
                  </TableCell>

                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    {report.precision}
                  </TableCell>

                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    {report.shutDowns}
                  </TableCell>
                </TableRow>

                {/* Mobile Collapsed Row */}
                <TableRow sx={{ display: { xs: "table-row", md: "none" } }}>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={3}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                    }}
                  >
                    <Collapse
                      in={expandedRows[unitId]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ py: 2, px: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Month:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {new Date(
                                report.year,
                                report.month - 1
                              ).toLocaleString("default", {
                                month: "long",
                              })}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Stop(Hrs):
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {report.down}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Run(Hrs):
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {report.up}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Customer(Hrs):
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {report.customer}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              PRC(Hrs):
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {report.precision}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Shutdowns:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {report.shutDowns}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                No units found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <Box>
        {loadingUnitSearchData && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              bgcolor: "rgba(187, 183, 183, 0.12)",
              backdropFilter: "blur(10px)",
              zIndex: 10,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress color="#60a5fa" size={70} />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mt: 1,
          mb: 2,
          px: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            fontWeight="bold"
            sx={{ fontSize: isSmallScreen ? 14 : 18 }}
          >
            Monthly Runtime Report
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Button
            sx={{ borderRadius: 0 }}
            variant="outlined"
            size={isSmallScreen ? "small" : "large"}
            color="white"
            startIcon={<GetAppIcon />}
            onClick={handleExportMenuOpen}
          >
            Export
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          mb: 2,
          px: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            label="Search Unit..."
            size="small"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 150, width: 200 }}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormControl sx={{ minWidth: 100, width: 150 }} size="small">
            <InputLabel id="month-select-label">Select Month</InputLabel>
            <Select
              labelId="month-select-label"
              value={selectedMonth}
              label="Select Month"
              onChange={(e) => {
                const chosen = lastSixMonths.find(
                  (m) => m.value === e.target.value
                );
                setSelectedMonth(chosen.value);
                setSelectedYear(chosen.year);
              }}
            >
              {lastSixMonths.map((month) => (
                <MenuItem key={month.label} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      {loadingReport ? (
        <Box
          sx={{
            width: "100%",
            minHeight: "200px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            zIndex: 10,
          }}
        >
          <CircularProgress color="#60a5fa" size={60} />
        </Box>
      ) : error ? (
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            color: "red",
            fontWeight: "bold",
            py: 2,
          }}
        >
          {error}
        </Box>
      ) : (
        <>
          {renderTable()}
          <TablePagination
            component="div"
            count={filteredReportData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            sx={{ mt: 1 }}
          />
        </>
      )}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportMenuClose}
      >
        <MenuItem onClick={copyToClipboard}>
          <ListItemIcon>
            <FileCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy</ListItemText>
        </MenuItem>
        <MenuItem onClick={exportToCSV}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={exportToExcel}>
          <ListItemIcon>
            <TableViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Excel</ListItemText>
        </MenuItem>
        <MenuItem onClick={exportToPDF}>
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>PDF</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default MonthlyReports;
